import { useState, useMemo } from 'react';
import { useMetaData, useApi } from '../hooks/useApi';
import { useCategory } from '../context/CategoryContext';
import { useMonth } from '../context/MonthContext';
import { Brand } from '../utils/api';
import { StackedBar, ChartCard, ChartLoader, ChartError } from '../components/Charts';
import { fmtMonth } from '../utils/theme';
import { last6, topNplusOthers } from '../utils/brandShare';
import { isRideHailing } from '../utils/oneHr';

export default function NationalBrandShareView() {
  const { category }  = useCategory();
  const { months, sessionTypes, latestMonth, loading: metaLoading } = useMetaData();
  const { month }     = useMonth();
  const [sessionType, setSessionType] = useState('transactional_activity');

  const selectedMonth = month || latestMonth;

  // Ride Hailing & Taxi always uses 1-hour sessions (legacy table, through Apr 2026).
  const is1hr = isRideHailing(category);

  const { data, loading, error } = useApi(
    () => Brand.appTrend(category, sessionType, is1hr ? '1hr' : undefined),
    { category, sessionType, is1hr },
    !!category && !!months.length,
  );

  // 1hr legacy data ends at Apr 2026, so derive the 6-month window from the data.
  const months6 = useMemo(() => {
    if (is1hr) return [...new Set((data || []).map(r => r.month_id))].sort().slice(-6);
    return last6(months, selectedMonth);
  }, [is1hr, data, months, selectedMonth]);

  const series = useMemo(
    () => (data ? topNplusOthers(data, months6, { valueKey: 'visits_15min' }) : []),
    [data, months6],
  );

  if (metaLoading) return <div className="loading-state"><div className="spinner" /> Loading…</div>;

  return (
    <div>
      <div className="filters-bar">
        <span className="filter-label">Session</span>
        <select className="filter-select" value={sessionType} onChange={e => setSessionType(e.target.value)}>
          {sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <ChartCard
        title={`National Brand Share of Sessions${is1hr ? ' (1-hour)' : ''}`}
        sub={`${category} · Total Nation · ${sessionType} · ${is1hr ? '1-hour sessions, through Apr 2026' : 'last 6 months'}`}
      >
        {loading  && <ChartLoader height={360} />}
        {error    && <ChartError message={error} height={360} />}
        {!loading && !error && series.length > 0 && (
          <StackedBar categories={months6.map(fmtMonth)} series={series} height={360} normalize />
        )}
        {!loading && !error && series.length === 0 && (
          <div className="empty-state">No session data for this category.</div>
        )}
      </ChartCard>
    </div>
  );
}
